import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Phone, Mail, ArrowUpRight } from "lucide-react";
import type { Lang } from "@/i18n";
import {
  SiFacebook,
  SiInstagram,
  SiYoutube,
  SiX,
  SiTelegram,
} from "react-icons/si";
import { FaLinkedinIn } from "react-icons/fa";
import type { IconType } from "react-icons";
import { useAuth } from "@/tg/AuthContext";
import { useI18n } from "@/i18n";
import { getAbout, type AboutData } from "@/api";
import { openLink, hapticTap } from "@/tg/init";
import { DONATE_URL } from "@/lib/links";
import Logo from "@/components/Logo";

// Brand icon map — keys match what the API returns
const SOCIAL_ICON_MAP: Record<string, { Icon: IconType; label: string }> = {
  facebook:  { Icon: SiFacebook,  label: "Facebook"  },
  instagram: { Icon: SiInstagram, label: "Instagram" },
  youtube:   { Icon: SiYoutube,   label: "YouTube"   },
  twitter:   { Icon: SiX,         label: "X"         },
  x:         { Icon: SiX,         label: "X"         },
  telegram:  { Icon: SiTelegram,  label: "Telegram"  },
  linkedin:  { Icon: FaLinkedinIn, label: "LinkedIn"  },
};

// All known socials show in the monochrome grid (Telegram included here, NOT in Links)
const EXCLUDED_FROM_GRID = new Set<string>();

const FALLBACK_ABOUT: AboutData = {
  hotline: "+38 044 3900 601",
  emails: ["help@prytulafoundation.org"],
  socials: {
    facebook:  "https://www.facebook.com/prytulafoundation.org",
    instagram: "https://www.instagram.com/prytulafoundation/",
    telegram:  "https://t.me/prytulafoundation",
    x:         "https://x.com/charityprytula",
    linkedin:  "https://www.linkedin.com/company/prytulafoundation/",
    youtube:   "https://www.youtube.com/@prytulafoundation",
  },
  disclaimer: "",
  official_site: "https://prytulafoundation.org",
  official_channel: "https://t.me/prytulafoundation",
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--tg-hint)",
  marginBottom: 8,
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "13px 16px",
  background: "var(--tg-secondary-bg)",
  borderRadius: 12,
  textDecoration: "none",
  color: "var(--tg-text)",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  border: "none",
  width: "100%",
  boxSizing: "border-box" as const,
  fontSize: 15,
};

const contactPillStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "12px 18px",
  background: "var(--tint)",
  borderRadius: "var(--r-pill)",
  color: "var(--ink)",
  textDecoration: "none",
  border: "none",
  width: "100%",
  boxSizing: "border-box" as const,
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  textAlign: "left" as const,
};

export default function SupportScreen() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { lang, setLang, t } = useI18n();

  const [about, setAbout] = useState<AboutData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getAbout(lang)
      .then((d) => setAbout(d))
      .catch(() => setAbout(null))
      .finally(() => setLoading(false));
  }, [lang]);

  const data = about ?? (loading ? null : FALLBACK_ABOUT);

  // Hotline href — strip all whitespace
  const telHref = data
    ? `tel:${data.hotline.replace(/\s/g, "")}`
    : "tel:+380443900601";

  // Socials filtered: exclude telegram, only include known brand icon keys
  const socialEntries = data
    ? Object.entries(data.socials).filter(
        ([key]) =>
          !EXCLUDED_FROM_GRID.has(key.toLowerCase()) &&
          key.toLowerCase() in SOCIAL_ICON_MAP,
      )
    : [];

  return (
    <div
      style={{
        background: "var(--tg-bg)",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Sticky header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          height: 56,
          background: "#ffffff",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
        }}
      >
        <Logo size={34} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Grey language pill — shows current lang */}
          <button
            onClick={() => { hapticTap(); setLang(lang === "uk" ? "en" : "uk" as Lang); }}
            aria-label="Switch language"
            style={{
              padding: "0 16px",
              height: 40,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "var(--r-pill)",
              background: "var(--tint)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {lang === "uk" ? "UA" : "EN"}
          </button>
          {/* Blue Donate pill */}
          <button
            onClick={() => { hapticTap(); openLink(DONATE_URL); }}
            aria-label="Donate"
            style={{
              padding: "0 16px",
              height: 40,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "var(--r-pill)",
              background: "var(--brand-blue)",
              color: "#ffffff",
              border: "none",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {t("donate")}
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{ padding: "20px 16px 40px", display: "flex", flexDirection: "column", gap: 24 }}>

        {/* Contacts section */}
        <section>
          <p style={sectionLabelStyle}>{t("contacts")}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <a href={loading ? "#" : telHref} style={contactPillStyle} aria-label={t("hotline")}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.2 }}>{t("hotline")}</span>
                <span style={{ fontSize: 15, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {data?.hotline ?? "+38 044 3900 601"}
                </span>
              </div>
              <Phone size={18} color="var(--muted)" strokeWidth={2} style={{ flexShrink: 0 }} />
            </a>
            {(() => {
              const email = (data?.emails ?? ["help@prytulafoundation.org"])[0];
              return (
                <a href={`mailto:${email}`} style={contactPillStyle} aria-label={email}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.2 }}>{t("contact_us")}</span>
                    <span style={{ fontSize: 15, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {email}
                    </span>
                  </div>
                  <Mail size={18} color="var(--muted)" strokeWidth={2} style={{ flexShrink: 0 }} />
                </a>
              );
            })()}
            <button
              onClick={() => openLink(data?.official_site ?? FALLBACK_ABOUT.official_site)}
              style={contactPillStyle}
              aria-label="Official site"
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.2 }}>Official site</span>
                <span style={{ fontSize: 15, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  prytulafoundation.org
                </span>
              </div>
              <ArrowUpRight size={18} color="var(--muted)" strokeWidth={2} style={{ flexShrink: 0 }} />
            </button>
          </div>
        </section>

        {/* Social networks icon grid */}
        {socialEntries.length > 0 && (
          <section>
            <p style={sectionLabelStyle}>{t("social_networks")}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {socialEntries.map(([key, url]) => {
                const entry = SOCIAL_ICON_MAP[key.toLowerCase()];
                if (!entry) return null;
                const { Icon, label } = entry;
                return (
                  <button
                    key={key}
                    onClick={() => openLink(url)}
                    aria-label={label}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      background: "var(--tint)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      WebkitTapHighlightColor: "transparent",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={24} color="var(--ink)" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Language row */}
        <section>
          <p style={sectionLabelStyle}>{t("language")}</p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "13px 16px",
              background: "var(--tint)",
              borderRadius: 12,
              border: "1px solid var(--border)",
            }}
          >
            <span style={{ fontSize: 15 }}>{t("language")}</span>
            <button
              onClick={() => { hapticTap(); setLang(lang === "uk" ? "en" : "uk" as Lang); }}
              style={{
                padding: "6px 12px",
                borderRadius: "var(--r-pill)",
                background: "#ffffff",
                color: "var(--text)",
                border: "1px solid var(--border)",
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1,
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {lang === "uk" ? "UA" : "EN"}
            </button>
          </div>
        </section>

        {/* Admin panel row — gated */}
        {auth?.is_admin && (
          <section>
            <button
              onClick={() => navigate("/admin")}
              style={{
                ...rowStyle,
                color: "var(--brand-blue)",
              }}
              aria-label={t("admin_panel")}
            >
              <span style={{ flex: 1, fontWeight: 600 }}>{t("admin_panel")}</span>
              <ChevronRight size={18} color="var(--brand-blue)" strokeWidth={2} />
            </button>
          </section>
        )}

        {/* Disclaimer */}
        {(data?.disclaimer || !loading) && (
          <p
            style={{
              fontSize: 12,
              lineHeight: 1.6,
              color: "var(--tg-hint)",
              margin: 0,
              textAlign: "center",
            }}
          >
            {data?.disclaimer || t("disclaimer")}
          </p>
        )}
      </div>
    </div>
  );
}
